import React, { useEffect, useRef, useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
} from '@mui/material'
import Button from '@mui/material/Button'
import useBaseService from '../../../../hooks/services/useBaseService'
import { entities } from '../../../../constants/entities'
import { useNavigate } from 'react-router-dom'

const RomaneioDetailsModal = ({
  open,
  data,
  onClose,
  onDownloadDocument,
  canDownloadInvoices,
  canManageInvoices,
  onManageInvoices,
}) => {
  const navigate = useNavigate()
  const { get: baseServiceGet } = useBaseService(navigate)
  const baseServiceGetRef = useRef(baseServiceGet)
  const [romaneios, setRomaneios] = useState([])
  const [shipmentVolumes, setShipmentVolumes] = useState([])
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    baseServiceGetRef.current = baseServiceGet
  }, [baseServiceGet])

  useEffect(() => {
    let cancelled = false
    if (!open || !data?.id) {
      setRomaneios([])
      setShipmentVolumes([])
      setIsLoading(false)
      return () => { cancelled = true }
    }

    setIsLoading(true)
    setRomaneios([])
    setShipmentVolumes([])
    Promise.allSettled([
      baseServiceGetRef.current(entities.shipments.romaneio.get(data.id)),
      baseServiceGetRef.current(entities.shipments.volumes.get(data.id), {
        page: 1,
        perPage: 1000,
      }),
    ])
      .then(([romaneioResult, volumesResult]) => {
        const romaneioResponse = romaneioResult.status === 'fulfilled'
          ? romaneioResult.value
          : null
        const volumesResponse = volumesResult.status === 'fulfilled'
          ? volumesResult.value
          : null
        const result = romaneioResponse?.result || romaneioResponse?.romaneio || []
        const volumes = volumesResponse?.result?.data || volumesResponse?.result || []
        if (!cancelled) {
          setRomaneios(Array.isArray(result) ? result : result ? [result] : [])
          setShipmentVolumes(Array.isArray(volumes) ? volumes : [])
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })
    return () => { cancelled = true }
  }, [open, data?.id])

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Detalhes do Romaneio</DialogTitle>
      <DialogContent>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : !romaneios.length ? (
          <Typography>Nenhum romaneio encontrado para este carregamento.</Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {romaneios.map((romaneio) => {
              const items = romaneio?.items || []
              const volumesById = new Map(
                shipmentVolumes.map((volume) => [String(volume.id), volume]),
              )
              return <Paper key={romaneio.id || romaneio.movement_id} variant="outlined" sx={{ p: 2 }}>
            <Typography variant="h6">Cabeçalho</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
              <Typography><strong>Cliente:</strong> {romaneio.customer_name}</Typography>
              <Typography><strong>CNPJ:</strong> {romaneio.customer_cnpj}</Typography>
              <Typography sx={{ gridColumn: '1 / -1' }}><strong>Endereço:</strong> {romaneio.customer_address}</Typography>
              <Typography><strong>Tipo:</strong> {romaneio.type}</Typography>
              <Typography><strong>Empresa:</strong> {romaneio.company}</Typography>
              <Typography><strong>Motorista:</strong> {romaneio.driver_name}</Typography>
              <Typography><strong>Veículo:</strong> {romaneio.vehicle_number}</Typography>
              <Typography><strong>Status:</strong> {romaneio.status_label || romaneio.status}</Typography>
            </Box>

            <Typography variant="h6" sx={{ mt: 2 }}>Itens</Typography>
            {(romaneio.documents || []).filter((document) => String(document.document_type || document.type || '').toUpperCase().includes('NOTA') && !document.deleted_at).map((document) => <Box key={document.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mt: 1 }}><Typography>{document.original_file_name || 'Nota Fiscal'}</Typography>{canDownloadInvoices && <Button size="small" onClick={() => onDownloadDocument?.(romaneio, document)}>Baixar NF</Button>}</Box>)}
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Tipo</TableCell>
                    <TableCell>Quantidade</TableCell>
                    <TableCell>Peso</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((item, idx) => {
                    const label = String(item.type || '').trim().toLowerCase()
                    const labelId = label.match(/^\s*(\d+)\s*[-–]/)?.[1]
                    const volume = (item.volume_id != null &&
                      volumesById.get(String(item.volume_id))) ||
                      (labelId && (volumesById.get(labelId) || shipmentVolumes.find(
                        (candidate) => String(candidate.code) === labelId,
                      ))) ||
                      shipmentVolumes.find((candidate) => {
                        const candidateName = String(candidate.name || '').trim().toLowerCase()
                        const candidateCode = String(candidate.code || '').trim().toLowerCase()
                        return candidateName && label.includes(candidateName) ||
                          candidateCode && label.includes(candidateCode)
                      })
                    const volumeItems = volume?.items || []

                    return (
                      <React.Fragment key={`${item.volume_id ?? item.type}-${idx}`}>
                        <TableRow>
                          <TableCell>{item.type}</TableCell>
                          <TableCell>{item.quantity}</TableCell>
                          <TableCell>{item.weight}</TableCell>
                        </TableRow>
                        {volumeItems.length > 0 && (
                          <TableRow>
                            <TableCell colSpan={3} sx={{ pl: 4, fontWeight: 600, bgcolor: 'action.hover' }}>
                              Itens do volume
                            </TableCell>
                          </TableRow>
                        )}
                        {volumeItems.map((volumeItem, volumeItemIndex) => {
                          const isEquipment = volumeItem.equipment_id != null
                          const name = isEquipment
                            ? `${volumeItem.equipment?.number ?? ''} - ${volumeItem.equipment?.name ?? 'Equipamento'}`
                            : [
                                volumeItem.product_item?.product?.name,
                                volumeItem.product_item?.category?.name,
                                volumeItem.product_item?.condition?.name,
                              ].filter(Boolean).join(' | ') || 'Insumo'

                          return (
                            <TableRow key={`${volume?.id}-${volumeItem.id ?? volumeItemIndex}`}>
                              <TableCell sx={{ pl: 4 }}>{name}</TableCell>
                              <TableCell>{isEquipment ? 1 : volumeItem.quantity ?? '-'}</TableCell>
                              <TableCell>-</TableCell>
                            </TableRow>
                          )
                        })}
                      </React.Fragment>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
              <Chip label={`Volumes: ${romaneio.volumes}`} />
              <Chip label={`Peso Total: ${romaneio.total_weight}`} />
            </Box>
            {canManageInvoices && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Button variant="contained" onClick={() => onManageInvoices?.(romaneio)}>
                  {romaneio.documents?.some((document) =>
                    String(document.document_type || document.type || '').toUpperCase() === 'NOTA_FISCAL' &&
                    !document.deleted_at &&
                    document.active !== false
                  ) ? 'Substituir Nota Fiscal' : 'Anexar Nota Fiscal'}
                </Button>
              </Box>
            )}
              </Paper>
            })}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Fechar</Button>
      </DialogActions>
    </Dialog>
  )
}

export default RomaneioDetailsModal
